import { useEffect, useState } from "react";
import { Phone, Mail, MapPin, Clock, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import PageHero from "@/components/PageHero";
import SectionHeading from "@/components/SectionHeading";
import { Reveal } from "@/components/Reveal";
import { api } from "@/lib/api";
import { SITE, waLink } from "@/lib/site";

export default function Contact() {
  const [form, setForm] = useState({ name: "", phone: "", email: "", message: "" });
  const [sending, setSending] = useState(false);

  useEffect(() => {
    document.title = "Contact Us | Sagar Darshan Guest House, Somnath Gujarat";
  }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.contact(form);
      toast.success("Enquiry sent. We will get back to you shortly.");
      setForm({ name: "", phone: "", email: "", message: "" });
    } catch {
      toast.error("Could not send your enquiry. Please call or WhatsApp us instead.");
    } finally {
      setSending(false);
    }
  };

  const INFO = [
    { icon: Phone, label: "Phone", value: SITE.phoneDisplay, href: `tel:${SITE.phoneHref}`, testid: "contact-phone" },
    { icon: MessageCircle, label: "WhatsApp", value: SITE.phoneDisplay, href: waLink("Hello Sagar Darshan Guest House, I would like to enquire about a stay."), testid: "contact-whatsapp" },
    { icon: Mail, label: "Email", value: SITE.email, href: `mailto:${SITE.email}`, testid: "contact-email" },
    { icon: MapPin, label: "Address", value: SITE.address, testid: "contact-address" },
    { icon: Clock, label: "Check-in", value: "24-hour basis · refundable security deposit at check-in", testid: "contact-checkin" },
  ];

  return (
    <div data-testid="contact-page">
      <PageHero overline="Contact Us" title="We're Here to Help"
        image="https://customer-assets-gfyr7b9c.emergentagent.net/job_somnath-coastal-stay/artifacts/8phsefz9_IMG_0887.jpeg" />

      <section className="mx-auto max-w-7xl px-5 py-24 lg:px-10 lg:py-32">
        <div className="grid gap-14 lg:grid-cols-2">
          <div>
            <SectionHeading number="01" overline="Get in Touch" title="Speak With Our Team" />
            <div className="mt-10 space-y-4">
              {INFO.map((item, i) => (
                <Reveal key={item.label} delay={i * 0.07}>
                  <div className="flex items-start gap-5 border border-navy/10 bg-white p-6">
                    <span className="flex h-11 w-11 shrink-0 items-center justify-center border border-gold/40 text-gold-dark">
                      <item.icon size={18} strokeWidth={1.4} />
                    </span>
                    <div>
                      <p className="text-[10px] uppercase tracking-[0.25em] text-navy/50">{item.label}</p>
                      {item.href ? (
                        <a href={item.href} data-testid={item.testid} target={item.href.startsWith("http") ? "_blank" : undefined} rel="noopener noreferrer"
                          className="mt-1 block text-sm font-medium text-navy transition-colors hover:text-gold-dark">
                          {item.value}
                        </a>
                      ) : (
                        <p data-testid={item.testid} className="mt-1 text-sm font-medium text-navy">{item.value}</p>
                      )}
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>

          <Reveal delay={0.15}>
            <form data-testid="contact-form" onSubmit={submit} className="border border-navy/10 bg-white p-8 lg:p-10">
              <p className="font-display text-2xl text-navy">Send an Enquiry</p>
              <p className="mt-2 text-sm text-navy/60">We usually respond within a few hours.</p>
              <div className="mt-8 grid gap-5">
                <div>
                  <label className="mb-1.5 block text-[10px] font-semibold uppercase tracking-[0.25em] text-navy/50" htmlFor="cf-name">Name</label>
                  <input id="cf-name" data-testid="contact-name-input" className="input-lux" value={form.name} onChange={set("name")} required minLength={2} />
                </div>
                <div className="grid gap-5 sm:grid-cols-2">
                  <div>
                    <label className="mb-1.5 block text-[10px] font-semibold uppercase tracking-[0.25em] text-navy/50" htmlFor="cf-phone">Phone</label>
                    <input id="cf-phone" data-testid="contact-phone-input" type="tel" className="input-lux" value={form.phone} onChange={set("phone")} required minLength={8} />
                  </div>
                  <div>
                    <label className="mb-1.5 block text-[10px] font-semibold uppercase tracking-[0.25em] text-navy/50" htmlFor="cf-email">Email</label>
                    <input id="cf-email" data-testid="contact-email-input" type="email" className="input-lux" value={form.email} onChange={set("email")} required />
                  </div>
                </div>
                <div>
                  <label className="mb-1.5 block text-[10px] font-semibold uppercase tracking-[0.25em] text-navy/50" htmlFor="cf-message">Message</label>
                  <textarea id="cf-message" data-testid="contact-message-input" rows={5} className="input-lux resize-none" value={form.message} onChange={set("message")} required minLength={5} />
                </div>
                <button type="submit" data-testid="contact-submit-btn" disabled={sending}
                  className="btn-primary w-full disabled:cursor-not-allowed disabled:opacity-60">
                  {sending ? "Sending..." : "Send Enquiry"}
                </button>
              </div>
            </form>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
